import {
  CHANNELS,
  PLATFORMS,
  type Channel,
  MAX_PAGES,
  MAX_VALUES,
  countryCode,
  isChannel,
  isPreset,
  rangeDays,
  type PageScope,
  type WebsitePeriod,
} from './queries';
import { ALL_SEL, PICK_SEP, affiliateOf, emptyLinks, isAll, type Sel } from './selection';
import { parseHostParam } from './quality/hosts';

export const FILTER_PARAMS = {
  period: 'period',
  landing: 'lp',
  page: 'page',
  source: 'source',
  platform: 'platform',
  link: 'link',
  affiliate: 'affiliate',
  campaign: 'campaign',
  country: 'country',
  host: 'host',
  hosts: 'hosts',
} as const;

const NO_LINK = 'none';
/** Leads every value of a filter that reads "every one but these". */
const OUT = '-';

export const DEFAULT_INTERVAL: WebsitePeriod = '7D';

const MAX_PATH_BYTES = 1024;
const utf8 = new TextEncoder();
/** A page path the service accepts: a leading `/`, no query, fragment or control character, at most 1024 bytes. */
const pagePath = (path: string): boolean =>
  path.startsWith('/') &&
  // eslint-disable-next-line no-control-regex
  !/[?#\u0000-\u001f\u007f-\u009f]/.test(path) &&
  utf8.encode(path).length <= MAX_PATH_BYTES;

/** The pages picked (or left out), as `landing:/path` / `site:/path`. */
export const pageKey = (p: PageScope): string => `${p.kind}:${p.path}`;
export const pageOfKey = (key: string): PageScope | null => {
  const at = key.indexOf(':');
  const kind = key.slice(0, at);
  const path = key.slice(at + 1);
  return (kind === 'landing' || kind === 'site') && pagePath(path) ? { kind, path } : null;
};

export type ChannelPick = { channel: Channel; platforms: 'all' | string[]; except: string[] };
export type LinkPick = {
  affiliates: { id: string; exceptLinks: string[] }[];
  exact: string[];
  noLink: boolean;
  allExcept: string[] | null;
  exceptAffiliates?: string[];
};
export type Selection = { channels: ChannelPick[] | null; links: LinkPick | null };
/**
 * The addresses the numbers count: the brand's default (its public addresses, or its internal ones when it has no
 * public one), none (an explicit empty choice, which asks for nothing), or these normalized hosts. Read in quality mode
 * only.
 */
export type HostSelection = { kind: 'default' } | { kind: 'none' } | { kind: 'some'; hosts: string[] };
export type WebsiteFilters = Selection & {
  interval: WebsitePeriod;
  pages: Sel;
  campaigns: Sel;
  countries: Sel;
  hosts: HostSelection;
};
export const NO_FILTERS_OF: Omit<WebsiteFilters, 'interval'> = {
  hosts: { kind: 'default' },
  pages: ALL_SEL,
  channels: null,
  links: null,
  campaigns: ALL_SEL,
  countries: ALL_SEL,
};

const isPeriod = (v: string | null): v is WebsitePeriod =>
  v !== null && (isPreset(v) || rangeDays(v, Date.now()) !== null);

/** An `aff_id` part from the address: letters and digits only (an underscore would cross into the next part). */
const affIdPart = (v: string): string | null => {
  const part = v.trim();
  return /^[0-9A-Za-z]{1,64}$/.test(part) ? part : null;
};

/**
 * One filter's values from the address: its mode from the first value (`-` leads every value left out), then each
 * value that reads in that mode and passes `read`, without repeats, up to `max`.
 */
const readSel = (raw: readonly string[], read: (v: string) => string | null, max = MAX_VALUES): Sel => {
  const values = raw.map((v) => v.trim()).filter(Boolean);
  if (values.length === 0) return ALL_SEL;
  const out = values[0].startsWith(OUT);
  const picked = [
    ...new Set(
      values
        .filter((v) => v.startsWith(OUT) === out)
        .map((v) => read(out ? v.slice(OUT.length) : v))
        .filter((v): v is string => v !== null),
    ),
  ].slice(0, max);
  if (picked.length === 0) return ALL_SEL;
  return { mode: out ? 'out' : 'in', values: picked };
};

const safeValue = (v: string): string | null =>
  /^[A-Za-z0-9][A-Za-z0-9._~:@/+%-]{0,255}$/.test(v) && !['__proto__', 'constructor', 'prototype'].includes(v)
    ? v
    : null;
const platformValue = (channel: Channel, value: string): string | null => {
  if (!safeValue(value) || channel === 'Direct' || channel === 'Unknown') return null;
  return Object.hasOwn(PLATFORMS, channel) && !PLATFORMS[channel]?.includes(value) ? null : value;
};
const readChannels = (p: URLSearchParams): ChannelPick[] | null => {
  if (p.get('ch') === 'none') return [];
  const modern = p.has('ch') || p.has('chx');
  const raw = modern ? p.getAll('ch') : p.getAll('source');
  const picks = new Map<Channel, ChannelPick>();
  let budget = MAX_VALUES;
  const add = (v: string, exclude = false): void => {
    if (budget <= 0) return;
    const [channel, key, extra] = v.split(PICK_SEP);
    if (!isChannel(channel) || extra !== undefined) return;
    if (key === undefined && !exclude) {
      picks.set(channel, { channel, platforms: 'all', except: [] });
      budget--;
      return;
    }
    if (!key || !platformValue(channel, key)) return;
    const prev = picks.get(channel);
    if (exclude) {
      if (prev?.platforms === 'all' && !prev.except.includes(key)) {
        prev.except.push(key);
        budget--;
      }
    } else if (!prev) {
      picks.set(channel, { channel, platforms: [key], except: [] });
      budget--;
    } else if (prev.platforms !== 'all' && !prev.platforms.includes(key)) {
      prev.platforms.push(key);
      budget--;
    }
  };
  if (!modern && raw.length === 1 && isChannel(raw[0]) && p.getAll('platform').some((v) => !v.includes(PICK_SEP))) {
    p.getAll('platform').forEach((v) => add(`${raw[0]}${PICK_SEP}${v}`));
    if (!picks.size) add(raw[0]);
  } else if (modern) {
    raw.filter((v) => !v.startsWith('-')).forEach((v) => add(v));
  } else {
    const legacy = readSel([...raw, ...p.getAll('platform')], (v) => {
      const [channel, key, extra] = v.split(PICK_SEP);
      return isChannel(channel) && extra === undefined && (key === undefined || platformValue(channel, key)) ? v : null;
    });
    if (legacy.mode === 'in') legacy.values.forEach((v) => add(v));
    else if (legacy.values.length) {
      const keys = [...new Set(legacy.values.flatMap((v) => v.split(PICK_SEP).slice(1)))];
      for (const channel of CHANNELS.filter((c) => !legacy.values.includes(c))) {
        picks.set(channel, { channel, platforms: 'all', except: keys.filter((key) => platformValue(channel, key)) });
      }
    }
  }
  p.getAll('chx').forEach((v) => add(v, true));
  return picks.size ? [...picks.values()] : null;
};
const readLinks = (p: URLSearchParams): LinkPick | null => {
  const pick = emptyLinks();
  if (p.get('links') === 'none') return pick;
  let budget = MAX_VALUES;
  const push = (list: string[], raw: string, read = safeValue): void => {
    const v = read(raw);
    if (budget > 0 && v !== null && !list.includes(v)) {
      list.push(v);
      budget--;
    }
  };
  const affs: string[] = [];
  [...p.getAll('aff'), ...p.getAll('affiliate')].forEach((v) => push(affs, v, affIdPart));
  pick.affiliates = affs.map((id) => ({ id, exceptLinks: [] }));
  p.getAll('affx').forEach((id) => {
    const parent = pick.affiliates.find((a) => a.id === affiliateOf(id));
    if (parent) push(parent.exceptLinks, id);
  });
  if (p.get('links') === 'except' || p.has('linkx') || p.getAll('link').some((v) => v.startsWith('-'))) {
    pick.allExcept = [];
    pick.noLink = p.get('links') !== 'except' && !p.getAll('link').includes('-none');
    [
      ...p.getAll('linkx'),
      ...p
        .getAll('link')
        .filter((v) => v.startsWith('-') && v !== '-none')
        .map((v) => v.slice(1)),
    ].forEach((v) => push(pick.allExcept ?? [], v));
    pick.affiliates = [];
  }
  p.getAll('link')
    .filter((v) => !v.startsWith('-'))
    .forEach((v) => {
      if (v === 'none') pick.noLink = true;
      else if (pick.allExcept === null) push(pick.exact, v);
    });
  // `exact` escapes the old reserved `link=none` value for a literal link named "none".
  if (pick.allExcept === null) p.getAll('exact').forEach((v) => push(pick.exact, v));
  const exceptAffiliates: string[] = [];
  p.getAll('affout').forEach((v) => push(exceptAffiliates, v, affIdPart));
  if (!p.has('links') && !p.has('link') && !p.has('exact') && !p.has('aff') && !p.has('campaign')) {
    const legacy = readSel(p.getAll('affiliate'), affIdPart);
    if (legacy.mode === 'out') legacy.values.forEach((v) => push(exceptAffiliates, v, affIdPart));
    if (exceptAffiliates.length) pick.noLink = true;
  }
  if (exceptAffiliates.length) {
    pick.exceptAffiliates = exceptAffiliates;
    pick.allExcept ??= [];
    pick.affiliates = [];
    p.getAll('exact').forEach((v) => push(pick.exact, v));
  }
  return pick.affiliates.length || pick.exact.length || pick.noLink || pick.allExcept !== null ? pick : null;
};

/** The filters an address holds; unknown or malformed values are left out. */
export const readFilters = (params: URLSearchParams): WebsiteFilters => {
  const period = params.get(FILTER_PARAMS.period);
  const pages = readSel(
    [
      ...params
        .getAll(FILTER_PARAMS.landing)
        .map((v) => (v.startsWith(OUT) ? `${OUT}landing:${v.slice(1)}` : `landing:${v}`)),
      ...params.getAll(FILTER_PARAMS.page).map((v) => (v.startsWith(OUT) ? `${OUT}site:${v.slice(1)}` : `site:${v}`)),
    ],
    (v) => (pageOfKey(v) ? v : null),
    MAX_PAGES,
  );
  const channels = readChannels(params);
  const links = readLinks(params);
  const campaigns = links === null ? readSel(params.getAll(FILTER_PARAMS.campaign), affIdPart) : ALL_SEL;
  const countries = readSel(params.getAll(FILTER_PARAMS.country), countryCode);
  return {
    interval: isPeriod(period) ? period : DEFAULT_INTERVAL,
    pages,
    channels,
    links,
    campaigns,
    countries,
    hosts: parseHostParam(params),
  };
};

/** One filter as the address's `name` parameters (every value led by `-` when it reads "every one but these"). */
const setSel = (out: URLSearchParams, name: string, sel: Sel, write: (v: string) => string = (v) => v): void => {
  out.delete(name);
  if (isAll(sel)) return;
  sel.values.forEach((v) => out.append(name, `${sel.mode === 'out' ? OUT : ''}${write(v)}`));
};

/** The address's parameters with `next` applied (other parameters kept). */
export const writeFilters = (params: URLSearchParams, next: Partial<WebsiteFilters>): URLSearchParams => {
  const out = new URLSearchParams(params);
  if (next.interval !== undefined) {
    if (next.interval === DEFAULT_INTERVAL) out.delete(FILTER_PARAMS.period);
    else out.set(FILTER_PARAMS.period, next.interval);
  }
  if (next.pages !== undefined) {
    const { mode, values } = next.pages;
    const of = (kind: PageScope['kind']): Sel => ({
      mode,
      values: values.flatMap((k) => {
        const page = pageOfKey(k);
        return page && page.kind === kind ? [page.path] : [];
      }),
    });
    // A kind with nothing listed under "these" leaves its parameter out (the other kind lists the pages).
    const landing = of('landing');
    const site = of('site');
    setSel(out, FILTER_PARAMS.landing, landing.values.length > 0 ? landing : ALL_SEL);
    setSel(out, FILTER_PARAMS.page, site.values.length > 0 ? site : ALL_SEL);
  }
  if (next.channels !== undefined) {
    ['source', 'platform', 'ch', 'chx'].forEach((k) => out.delete(k));
    if (next.channels?.length === 0) out.set('ch', 'none');
    next.channels?.forEach((c) => {
      if (c.platforms === 'all') {
        out.append('ch', c.channel);
        c.except.forEach((k) => out.append('chx', `${c.channel}${PICK_SEP}${k}`));
      } else c.platforms.forEach((k) => out.append('ch', `${c.channel}${PICK_SEP}${k}`));
    });
  }
  if (next.links !== undefined) {
    ['link', 'links', 'linkx', 'exact', 'affiliate', 'aff', 'affx', 'affout'].forEach((k) => out.delete(k));
    const l = next.links;
    if (l) {
      if (l.allExcept !== null) {
        out.set('links', 'except');
        l.allExcept.forEach((id) => out.append('linkx', id));
        l.exceptAffiliates?.forEach((id) => out.append('affout', id));
        l.exact.forEach((id) => out.append('exact', id));
      } else {
        l.affiliates.forEach((a) => {
          out.append('aff', a.id);
          a.exceptLinks.forEach((id) => out.append('affx', id));
        });
        l.exact.forEach((id) => out.append(id === 'none' ? 'exact' : 'link', id));
        if (!l.affiliates.length && !l.exact.length && !l.noLink) out.set('links', 'none');
      }
      if (l.noLink) out.append('link', 'none');
    }
  }
  if (next.campaigns !== undefined) setSel(out, FILTER_PARAMS.campaign, next.campaigns);
  if (next.countries !== undefined) setSel(out, FILTER_PARAMS.country, next.countries);
  if (next.hosts !== undefined) {
    out.delete(FILTER_PARAMS.host);
    out.delete(FILTER_PARAMS.hosts);
    if (next.hosts.kind === 'none') out.set(FILTER_PARAMS.hosts, 'none');
    else if (next.hosts.kind === 'some') next.hosts.hosts.forEach((h) => out.append(FILTER_PARAMS.host, h));
  }
  return out;
};
